import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { pageConfigBySlug } from "../../services/nav/NavigationService";
import {
  INextGenDashboardChart,
  INextGenDashboardKpi,
  INextGenDataSourceStatus,
} from "../../services/zeos-api/contracts/Dashboard";
import { ChartCard } from "./ChartCard";
import { DataStatusStrip } from "./DataStatusStrip";
import { FilterField } from "./DetailFilterBar";
import { FilterPropertySelect } from "./FilterPropertySelect";
import { KpiStrip } from "./KpiStrip";
import { MidFiChip } from "./MidFiChip";
import { MF } from "./midFiTokens";

// Sheet 06 (Demand Response / VPP), approved as a placeholder (DB-F5), at mid-fi (4019, UX-05). The
// page ships in its unified empty state with no dependency on DR data, and fills in when a DR program
// integration lands. Nothing here is property data: program rows point at screening rather than
// claiming eligibility, and the curtailment chart is a labelled pattern preview.

const DATA_STATUS: INextGenDataSourceStatus[] = [
  {
    source: "dr_telemetry",
    label: "DR telemetry",
    status: "not_connected",
    message: "not yet integrated",
  },
  {
    source: "eligibility",
    label: "Eligibility",
    status: "ok",
    message: "via the Incentive engine, territory-aware",
  },
];

const pendingKpi = (
  key: string,
  label: string,
  unit: string,
  badge: string,
  caption: string
): INextGenDashboardKpi => ({
  key,
  label,
  value: 0,
  unit,
  trend: "na",
  status: "pending",
  caption,
  badge: { label: badge, tone: "neutral" },
});

// Labels and captions from sheet 06.
const KPIS: INextGenDashboardKpi[] = [
  pendingKpi("dr_enrollment_status", "Enrollment Status", "", "Not enrolled", "eligible programs surfaced by the Incentive engine"),
  pendingKpi("dr_curtailment_delivered", "Curtailment Delivered (kW)", "kW", "No DR program connected", "per event · verified vs committed"),
  pendingKpi("dr_event_performance", "Event Performance (%)", "%", "No DR program connected", "delivered / committed"),
  pendingKpi("dr_program_revenue", "Program Revenue ($)", "$", "No DR program connected", "capacity + performance payments"),
];

const placeholderChart = (chart: Partial<INextGenDashboardChart> & { title: string }): INextGenDashboardChart => ({
  type: "line",
  yAxisLabel: "",
  xAxisLabel: "",
  series: [],
  ...chart,
});

const PROGRAMS_CARD = placeholderChart({
  title: "Eligible Programs",
  eyebrow: "DR · Programs",
  subtitle: "Territory-aware eligibility from the Incentive engine: the activation path for this tile",
  questions: [
    { text: "What could demand response earn this property?", kind: "canned" },
    { text: "What would automated demand response earn through my building controls?", kind: "model" },
  ],
});

const CURTAILMENT_CARD = placeholderChart({
  title: "Event Curtailment Detail",
  eyebrow: "DR · M&V pattern",
  subtitle: "CBL baseline vs actual load inside the event window · interval resolution",
  questions: [{ text: "What did the building actually shed during the event?", kind: "canned" }],
});

const PhasePills = () => (
  <div
    className="flex rounded-[22px] border border-mf-border bg-white p-[3px]"
    role="group"
    aria-label="Season or year (available once a DR program is connected)"
    title="Available once a DR program is connected"
  >
    {["Season", "YR"].map((label, index) => (
      <span
        key={label}
        aria-disabled="true"
        className={`rounded-[18px] px-3.5 py-1.5 text-[12.5px] font-bold uppercase ${
          index === 0 ? "text-mf-ink shadow-[inset_0_0_0_1.5px_#25272E]" : "text-mf-sub"
        }`}
      >
        {label}
      </span>
    ))}
  </div>
);

interface IProgramRowProps {
  chip: React.ReactNode;
  text: string;
  action: React.ReactNode;
}

const ProgramRow = ({ chip, text, action }: IProgramRowProps) => (
  <div className="flex flex-wrap items-center gap-3 border-t border-mf-border py-3 first:border-t-0">
    {chip}
    <span className="min-w-0 flex-1 text-[13px] font-semibold text-mf-ink">{text}</span>
    {action}
  </div>
);

const RoadmapLabel = () => <span className="text-xs font-bold text-mf-faint">On roadmap</span>;

// Baseline (dashed) vs actual (solid) across a day, dipping inside a shaded event window; the gap
// between them is the curtailment. Shape only, no values.
const CurtailmentPatternPreview = () => (
  <figure>
    <svg
      viewBox="0 0 600 230"
      className="h-auto max-h-[260px] w-full"
      role="img"
      aria-label="Illustrative pattern: actual load drops below the CBL baseline during the event window; the gap is the curtailment delivered."
    >
      <rect x="250" y="16" width="140" height="176" fill={MF.orangeSoft} rx="4" />
      <text x="262" y="34" fontSize="11" fontWeight="700" fill={MF.faint} fontFamily="Nunito">
        EVENT WINDOW
      </text>
      <line x1="20" y1="192" x2="580" y2="192" stroke={MF.border} strokeWidth="1.5" />
      <path
        d="M20 96 C 150 90, 240 86, 320 84 S 480 84, 580 88"
        fill="none"
        stroke={MF.faint}
        strokeWidth="2.5"
        strokeDasharray="7 6"
      />
      <path
        d="M20 100 L 250 92 L 285 150 L 350 156 L 390 96 L 580 94"
        fill="none"
        stroke={MF.ink}
        strokeWidth="2.5"
        strokeLinejoin="round"
      />
      <line x1="318" y1="86" x2="318" y2="152" stroke={MF.energy} strokeWidth="3" />
      <text x="262" y="58" fontSize="11" fontWeight="700" fill={MF.energyInk} fontFamily="Nunito">
        GAP = CURTAILMENT
      </text>
    </svg>
    <figcaption className="mt-1 flex flex-wrap gap-[18px] text-[11px] font-bold text-mf-sub">
      <span className="flex items-center gap-1.5">
        <span className="h-0 w-4 border-t-[2.5px] border-dashed border-mf-faint" aria-hidden="true" />
        CBL baseline
      </span>
      <span className="flex items-center gap-1.5">
        <span className="h-[3.5px] w-4 rounded bg-mf-ink" aria-hidden="true" />
        actual load
      </span>
      <span className="text-mf-faint">Pattern preview, not this property's data</span>
    </figcaption>
  </figure>
);

export const DemandResponsePlaceholder = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const goToIncentives = () => {
    const incentivesSlug = pageConfigBySlug("incentives")?.slug || "incentives";
    navigate(`/${incentivesSlug}?${searchParams.toString()}`);
  };

  return (
    <div className="mx-auto w-full max-w-[1600px] px-4 py-6 lg:px-6">
      <div
        className="mb-3.5 flex flex-wrap items-center gap-3.5"
        aria-label="Filters (available once a DR program is connected)"
      >
        <FilterPropertySelect />
        <FilterField
          label="Program"
          value="None enrolled"
          hint="Program selection is available once the property enrolls in a DR program."
          disabled
        />
        <PhasePills />
        <FilterField
          label="Roadmap"
          value="ADR · grid-interactive · VPP"
          hint="Planned DR capabilities for this page."
          className="lg:ml-auto"
        />
      </div>

      <div className="mb-4">
        <DataStatusStrip sources={DATA_STATUS} badge="Placeholder" note="Activates on DR enrollment" />
      </div>

      <div className="mb-4">
        <KpiStrip kpis={KPIS} compareMode="PriorPeriod" layout="grid" />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <ChartCard chart={PROGRAMS_CARD}>
          <ProgramRow
            chip={<MidFiChip label="Screen" tone="info" />}
            text="Utility demand-response programs for this property's territory"
            action={
              <button
                type="button"
                onClick={goToIncentives}
                className="text-[13px] font-extrabold text-mf-orange hover:underline"
              >
                Screen &amp; enroll →
              </button>
            }
          />
          <ProgramRow
            chip={<MidFiChip label="Future" tone="future" />}
            text="VPP aggregation: grid-interactive participation via the DER fleet"
            action={<RoadmapLabel />}
          />
          <ProgramRow
            chip={<MidFiChip label="Future" tone="future" />}
            text="Automated demand response (ADR): dispatch via building controls"
            action={<RoadmapLabel />}
          />
        </ChartCard>

        <ChartCard chart={CURTAILMENT_CARD} badge={<MidFiChip label="Illustrative" tone="future" />}>
          <CurtailmentPatternPreview />
        </ChartCard>
      </div>
    </div>
  );
};
